import type { Inspection } from '@/types';

const DB_NAME = 'wire3-offline';
const DB_VERSION = 2; // bumped to add pending-files store
const INSPECTIONS_STORE = 'queued-inspections';
const FILES_STORE = 'pending-files';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(INSPECTIONS_STORE)) {
        const store = db.createObjectStore(INSPECTIONS_STORE, { keyPath: 'id' });
        store.createIndex('queuedAt', 'queuedAt');
      }
      // New store for raw file bytes keyed by photo ID
      if (!db.objectStoreNames.contains(FILES_STORE)) {
        db.createObjectStore(FILES_STORE, { keyPath: 'photoId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export interface QueuedInspection {
  id: string;
  inspection: Inspection;
  queuedAt: string;
  attempts: number;
}

interface StoredFile {
  photoId: string;
  arrayBuffer: ArrayBuffer;
  mimeType: string;
  fileName: string;
}

/**
 * Queues an inspection for later sync.
 * Any photos with pendingFile attached will have their binary data
 * saved separately in the files store so they survive page reloads.
 */
export async function queueInspection(inspection: Inspection): Promise<void> {
  const db = await openDB();

  // Collect all pending files before stripping them from the inspection
  const filesToStore: StoredFile[] = [];

  // Deep clone inspection so we can safely strip non-serializable fields
  const serializable: Inspection = JSON.parse(JSON.stringify(inspection, (key, value) => {
    // Strip File objects and blob URIs — we'll restore them from the files store
    if (key === 'pendingFile') return undefined;
    return value;
  }));

  // Extract file bytes from pending photos in vehiclePhotos
  if (inspection.vehiclePhotos) {
    for (const vp of inspection.vehiclePhotos) {
      if (vp.photo?.pendingUpload && vp.photo.pendingFile) {
        const file = vp.photo.pendingFile;
        const arrayBuffer = await file.arrayBuffer();
        filesToStore.push({
          photoId: vp.photo.id,
          arrayBuffer,
          mimeType: file.type || 'image/jpeg',
          fileName: file.name || `${vp.promptId}.jpg`,
        });
      }
    }
  }

  // Extract file bytes from pending photos in check photos
  for (const cat of inspection.categories) {
    for (const check of cat.checks) {
      for (const photo of check.photos) {
        if (photo.pendingUpload && photo.pendingFile) {
          const file = photo.pendingFile;
          const arrayBuffer = await file.arrayBuffer();
          filesToStore.push({
            photoId: photo.id,
            arrayBuffer,
            mimeType: file.type || 'image/jpeg',
            fileName: file.name || `${check.id}.jpg`,
          });
        }
      }
    }
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction([INSPECTIONS_STORE, FILES_STORE], 'readwrite');

    // Store file bytes
    const filesStore = tx.objectStore(FILES_STORE);
    for (const f of filesToStore) {
      filesStore.put(f);
    }

    // Store inspection (without File objects)
    const inspStore = tx.objectStore(INSPECTIONS_STORE);
    const queued: QueuedInspection = {
      id: inspection.id,
      inspection: serializable,
      queuedAt: new Date().toISOString(),
      attempts: 0,
    };
    inspStore.put(queued);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Retrieves all queued inspections, restoring File objects
 * for any photos that have stored binary data.
 */
export async function getQueuedInspections(): Promise<QueuedInspection[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction([INSPECTIONS_STORE, FILES_STORE], 'readonly');
    const inspReq = tx.objectStore(INSPECTIONS_STORE).getAll();
    const filesReq = tx.objectStore(FILES_STORE).getAll();

    let inspections: QueuedInspection[] = [];
    let storedFiles: StoredFile[] = [];

    inspReq.onsuccess = () => { inspections = inspReq.result; };
    filesReq.onsuccess = () => { storedFiles = filesReq.result; };

    tx.oncomplete = () => {
      // Build a map of photoId -> File for quick lookup
      const fileMap = new Map<string, File>();
      for (const sf of storedFiles) {
        const blob = new Blob([sf.arrayBuffer], { type: sf.mimeType });
        const file = new File([blob], sf.fileName, { type: sf.mimeType });
        fileMap.set(sf.photoId, file);
      }

      // Restore File objects and blob URIs for pending photos
      for (const queued of inspections) {
        if (queued.inspection.vehiclePhotos) {
          for (const vp of queued.inspection.vehiclePhotos) {
            if (vp.photo?.pendingUpload) {
              const file = fileMap.get(vp.photo.id);
              if (file) {
                vp.photo.pendingFile = file;
                vp.photo.uri = URL.createObjectURL(file);
              }
            }
          }
        }

        for (const cat of queued.inspection.categories) {
          for (const check of cat.checks) {
            for (const photo of check.photos) {
              if (photo.pendingUpload) {
                const file = fileMap.get(photo.id);
                if (file) {
                  photo.pendingFile = file;
                  photo.uri = URL.createObjectURL(file);
                }
              }
            }
          }
        }
      }

      resolve(inspections);
    };

    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Removes a queued inspection and all its associated stored files.
 */
export async function removeQueuedInspection(id: string): Promise<void> {
  const db = await openDB();

  // First get the inspection to find its photo IDs
  const queued = await new Promise<QueuedInspection | undefined>((resolve, reject) => {
    const tx = db.transaction(INSPECTIONS_STORE, 'readonly');
    const req = tx.objectStore(INSPECTIONS_STORE).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  return new Promise((resolve, reject) => {
    const tx = db.transaction([INSPECTIONS_STORE, FILES_STORE], 'readwrite');

    // Delete inspection
    tx.objectStore(INSPECTIONS_STORE).delete(id);

    // Delete any associated files
    if (queued) {
      const filesStore = tx.objectStore(FILES_STORE);
      const photoIds: string[] = [];

      if (queued.inspection.vehiclePhotos) {
        for (const vp of queued.inspection.vehiclePhotos) {
          if (vp.photo?.pendingUpload && vp.photo.id) {
            photoIds.push(vp.photo.id);
          }
        }
      }
      for (const cat of queued.inspection.categories) {
        for (const check of cat.checks) {
          for (const photo of check.photos) {
            if (photo.pendingUpload && photo.id) {
              photoIds.push(photo.id);
            }
          }
        }
      }

      for (const photoId of photoIds) {
        filesStore.delete(photoId);
      }
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getQueueCount(): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(INSPECTIONS_STORE, 'readonly');
    const req = tx.objectStore(INSPECTIONS_STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}